import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  Sparkles, 
  FileText, 
  Eye, 
  Send, 
  MoreVertical,
  Copy,
  Trash2,
  ExternalLink,
  Clock,
  CheckCircle,
  Video,
  Palette,
  Building2,
  Crown,
  BarChart3,
  TrendingUp,
  Users,
  Link2,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Premium template previews
const DECK_TEMPLATES = [
  {
    id: 'modern-executive',
    name: 'Modern Executive',
    description: 'Minimalist luxury aesthetic with clean lines and refined typography',
    preview: 'linear-gradient(135deg, #FFFEF5 0%, #F5F5F0 100%)',
    accent: '#B89B5E',
    icon: Crown,
    style: 'minimalist'
  },
  {
    id: 'contemporary-hospitality',
    name: 'Contemporary Hospitality',
    description: 'Warm tones and lifestyle imagery for hotels and resorts',
    preview: 'linear-gradient(135deg, #FDF8F3 0%, #F0E6D8 100%)',
    accent: '#8B7355',
    icon: Building2,
    style: 'warm'
  },
  {
    id: 'corporate-prestige',
    name: 'Corporate Prestige',
    description: 'Dark backgrounds and bold typography for executive presentations',
    preview: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%)',
    accent: '#C9A962',
    icon: Palette,
    style: 'dark'
  }
];

export default function VisionDecks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewDeckDialog, setShowNewDeckDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newDeckName, setNewDeckName] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Send deck dialog state
  const [sendDeckDialog, setSendDeckDialog] = useState(false);
  const [deckToSend, setDeckToSend] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingDeck, setSendingDeck] = useState(false);
  const [unreadByDeck, setUnreadByDeck] = useState({});

  useEffect(() => {
    fetchDecks();
    fetchUnreadCounts();
  }, []);

  const fetchUnreadCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/lead-submissions/unread-counts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setUnreadByDeck(d?.counts || {});
      }
    } catch { /* silent */ }
  };

  const fetchDecks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/vision-decks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDecks(data);
      }
    } catch (error) {
      console.error('Error fetching vision decks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeck = async () => {
    if (!selectedTemplate || !newDeckName.trim()) {
      toast.error('Please select a template and enter a name');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/vision-decks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newDeckName,
          template_id: selectedTemplate.id
        })
      });

      if (response.ok) {
        const deck = await response.json();
        toast.success('Vision Deck created');
        setShowNewDeckDialog(false);
        setSelectedTemplate(null);
        setNewDeckName('');
        navigate(`/vision-decks/${deck.id}/edit`);
      } else {
        toast.error('Failed to create deck');
      }
    } catch (error) {
      toast.error('Error creating deck');
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicateDeck = async (deck) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/vision-decks/${deck.id}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Deck duplicated');
        fetchDecks();
      }
    } catch (error) {
      toast.error('Error duplicating deck');
    }
  };

  const handleDeleteDeck = async (deck) => {
    if (!window.confirm(`Delete "${deck.name}"? This cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/vision-decks/${deck.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Deck deleted');
        fetchDecks();
      }
    } catch (error) {
      toast.error('Error deleting deck');
    }
  };

  // Open send dialog for a deck
  const handleOpenSendDialog = (deck) => {
    setDeckToSend(deck);
    setEmailSubject(`Your Exclusive Vision Deck: ${deck.name}`);
    setEmailMessage(`I'm excited to share this exclusive Vision Deck presentation with you.\n\nThis deck showcases our premium approach tailored specifically for your needs.\n\nClick the button below to view your personalized presentation.`);
    setSendDeckDialog(true);
  };

  // Send deck via email
  const handleSendDeck = async () => {
    if (!recipientEmail.trim() || !emailSubject.trim()) {
      toast.error('Please enter recipient email and subject');
      return;
    }

    setSendingDeck(true);
    try {
      const token = localStorage.getItem('token');
      const publicUrl = `${window.location.origin}/view/deck/${deckToSend.id}`;
      
      // Create professional HTML email for vision deck
      const htmlBody = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; background-color: #f8f7f4; font-family: Georgia, 'Times New Roman', serif;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f8f7f4;">
        <tr>
            <td align="center" style="padding: 40px 20px;">
                <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="background-color: #ffffff; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.08);">
                    <!-- Header -->
                    <tr>
                        <td style="padding: 48px 48px 32px; text-align: center; border-bottom: 1px solid #e8e6e1;">
                            <h1 style="margin: 0; font-size: 28px; font-weight: 400; color: #1a1a1a; letter-spacing: -0.5px;">
                                Your Exclusive Vision Deck
                            </h1>
                            <p style="margin: 12px 0 0; font-size: 16px; color: #666; font-style: italic;">
                                ${deckToSend.name}
                            </p>
                        </td>
                    </tr>
                    <!-- Body -->
                    <tr>
                        <td style="padding: 40px 48px;">
                            <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.7; color: #333;">
                                ${emailMessage.replace(/\n/g, '<br>')}
                            </p>
                            <table role="presentation" cellspacing="0" cellpadding="0" style="margin: 32px auto;">
                                <tr>
                                    <td align="center" style="background-color: #C5A572; border-radius: 4px;">
                                        <a href="${publicUrl}" target="_blank" style="display: inline-block; padding: 16px 40px; font-size: 15px; color: #ffffff; text-decoration: none; font-weight: 500; letter-spacing: 0.5px;">
                                            View Your Vision Deck
                                        </a>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    <!-- Footer -->
                    <tr>
                        <td style="padding: 32px 48px; background-color: #faf9f7; border-top: 1px solid #e8e6e1; text-align: center;">
                            <p style="margin: 0; font-size: 13px; color: #888;">
                                Powered by DH Design Premium Hospitality Growth OS
                            </p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;
      
      // Send email with styled HTML
      const response = await fetch(`${API}/api/gmail/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: recipientEmail,
          subject: emailSubject,
          body: htmlBody,
          deck_id: deckToSend.id
        })
      });

      if (response.ok) {
        // Update deck status to sent
        await fetch(`${API}/api/vision-decks/${deckToSend.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: 'sent' })
        });

        toast.success('Vision Deck sent successfully!');
        setSendDeckDialog(false);
        setDeckToSend(null);
        setRecipientEmail('');
        setEmailSubject('');
        setEmailMessage('');
        fetchDecks();
      } else {
        const errorData = await response.json();
        const errorMessage = typeof errorData === 'string' 
          ? errorData 
          : errorData.detail || errorData.message || errorData.error || 'Failed to send email';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error sending deck:', error);
      const errorMessage = error?.message || 'Error sending deck';
      toast.error(errorMessage);
    } finally {
      setSendingDeck(false);
    }
  };

  // Copy deck link to clipboard
  const handleCopyLink = (deck) => {
    const publicUrl = `${window.location.origin}/view/deck/${deck.id}`;
    navigator.clipboard.writeText(publicUrl);
    toast.success('Link copied to clipboard!');
  };

  const filteredDecks = decks.filter(deck =>
    deck.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    deck.opportunity_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Analytics summary
  const analyticsStats = {
    totalDecks: decks.length,
    publishedDecks: decks.filter(d => d.status === 'published' || d.status === 'sent').length,
    totalViews: decks.reduce((sum, d) => sum + (d.analytics?.prospect_views || 0), 0),
    recentlyViewed: decks.filter(d => {
      if (!d.analytics?.last_prospect_view) return false;
      const lastView = new Date(d.analytics.last_prospect_view);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return lastView > sevenDaysAgo;
    }).length
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline" className="text-muted-foreground"><Clock className="w-3 h-3 mr-1" /> Draft</Badge>;
      case 'published':
        return <Badge className="bg-[#B89B5E] text-white"><CheckCircle className="w-3 h-3 mr-1" /> Published</Badge>;
      case 'sent':
        return <Badge className="bg-emerald-600 text-white"><Send className="w-3 h-3 mr-1" /> Sent</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-light tracking-tight text-foreground flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-[#B89B5E]" />
              Vision Decks
            </h1>
            <p className="text-muted-foreground mt-1">
              Create stunning visual proposals that transform client possibilities
            </p>
          </div>
          
          <Dialog open={showNewDeckDialog} onOpenChange={setShowNewDeckDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#B89B5E] hover:bg-[#A08050] text-white" data-testid="create-vision-deck-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Vision Deck
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-light">Create Vision Deck</DialogTitle>
                <DialogDescription>
                  Select a premium template to begin crafting your client's transformation story
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Deck Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="e.g., Fairmont Whistler Uniform Collection"
                    value={newDeckName}
                    onChange={(e) => setNewDeckName(e.target.value)}
                    className="text-lg"
                    data-testid="new-deck-name-input"
                    autoFocus
                  />
                  {!newDeckName.trim() && selectedTemplate && (
                    <p className="text-sm text-amber-600 mt-1">Please enter a name for your Vision Deck</p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Select Template <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {DECK_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      data-testid={`template-${template.id}`}
                      className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                        selectedTemplate?.id === template.id
                          ? 'border-[#B89B5E] ring-2 ring-[#B89B5E]/20'
                          : 'border-border hover:border-[#B89B5E]/50'
                      }`}
                    >
                      <div 
                        className="h-24 rounded-md mb-3 flex items-center justify-center"
                        style={{ background: template.preview }}
                      >
                        <template.icon className={`w-8 h-8 ${template.style === 'dark' ? 'text-[#C9A962]' : 'text-[#B89B5E]'}`} />
                      </div>
                      <h3 className="font-medium text-foreground">{template.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                      {selectedTemplate?.id === template.id && (
                        <div className="absolute top-2 right-2">
                          <CheckCircle className="w-5 h-5 text-[#B89B5E]" />
                        </div>
                      )}
                    </button>
                  ))}
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewDeckDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateDeck} 
                  disabled={!selectedTemplate || !newDeckName.trim() || creating}
                  className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
                  data-testid="create-deck-submit"
                >
                  {creating ? 'Creating...' : 'Create Vision Deck'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Analytics Summary */}
        {!loading && decks.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#B89B5E]/10">
                  <FileText className="w-5 h-5 text-[#B89B5E]" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">{analyticsStats.totalDecks}</p>
                  <p className="text-xs text-muted-foreground">Total Decks</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">{analyticsStats.publishedDecks}</p>
                  <p className="text-xs text-muted-foreground">Published</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Eye className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">{analyticsStats.totalViews}</p>
                  <p className="text-xs text-muted-foreground">Prospect Views</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">{analyticsStats.recentlyViewed}</p>
                  <p className="text-xs text-muted-foreground">Active (7 days)</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search vision decks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 max-w-md"
            data-testid="search-decks-input"
          />
        </div>

        {/* Vision Studio Section */}
        <Card className="mb-8 bg-gradient-to-r from-[#1A1A1A] to-[#2D2D2D] border-[#B89B5E]/30">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-[#B89B5E]/20">
                  <Video className="w-6 h-6 text-[#B89B5E]" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Vision Studio</h3>
                  <p className="text-gray-400 text-sm mt-1">
                    Transform your proposals into cinematic experiences. Create AI-powered 8-10 second video clips 
                    that showcase your uniform collections with stunning visual storytelling.
                  </p>
                </div>
              </div>
              <Button 
                asChild
                className="bg-[#B89B5E] hover:bg-[#A08050] text-white whitespace-nowrap"
                data-testid="launch-vision-studio"
              >
                <a href="https://labs.google/flow/about" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Launch Vision Studio
                </a>
              </Button>
            </div>
            
            <div className="mt-4 pt-4 border-t border-white/10">
              <h4 className="text-sm font-medium text-[#B89B5E] mb-2">Quick Start Guide</h4>
              <ol className="text-sm text-gray-400 space-y-1">
                <li>1. Upload reference images of your uniform collection</li>
                <li>2. Select a cinematic style</li>
                <li>3. Generate an 8-10 second showcase clip</li>
                <li>4. Download and embed directly into your Vision Deck</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Decks Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredDecks.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Sparkles className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Vision Decks Yet</h3>
              <p className="text-muted-foreground text-center max-w-md mb-4">
                Vision Decks help you present transformative uniform solutions to your clients. 
                Create your first deck to start winning more deals.
              </p>
              <Button 
                onClick={() => setShowNewDeckDialog(true)}
                className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Vision Deck
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDecks.map((deck) => {
              const template = DECK_TEMPLATES.find(t => t.id === deck.template_id) || DECK_TEMPLATES[0];
              
              return (
                <Card 
                  key={deck.id} 
                  className="group hover:shadow-lg transition-all duration-300 overflow-hidden"
                  data-testid={`deck-card-${deck.id}`}
                >
                  {/* Preview Header */}
                  <div 
                    className="h-32 flex items-center justify-center relative"
                    style={{ background: template.preview }}
                  >
                    <template.icon className={`w-10 h-10 ${template.style === 'dark' ? 'text-[#C9A962]' : 'text-[#B89B5E]'}`} />
                    <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                      {getStatusBadge(deck.status)}
                      {(unreadByDeck[deck.id] || 0) > 0 && (
                        <Link to="/lead-submissions" onClick={(e) => e.stopPropagation()} className="bg-[#B89B5E] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow" data-testid={`deck-unread-badge-${deck.id}`}>
                          {unreadByDeck[deck.id]} new {unreadByDeck[deck.id] === 1 ? 'submission' : 'submissions'}
                        </Link>
                      )}
                    </div>
                  </div>
                  
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-foreground truncate">{deck.name}</h3>
                        {deck.opportunity_name && (
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            <Building2 className="w-3 h-3 inline mr-1" />
                            {deck.opportunity_name}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {deck.page_count || 0} pages • {template.name}
                        </p>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/vision-decks/${deck.id}/edit`}>
                              <FileText className="w-4 h-4 mr-2" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/vision-decks/${deck.id}/preview`}>
                              <Eye className="w-4 h-4 mr-2" />
                              Preview
                            </Link>
                          </DropdownMenuItem>
                          {deck.status === 'published' && (
                            <>
                              <DropdownMenuItem onClick={() => handleCopyLink(deck)}>
                                <Link2 className="w-4 h-4 mr-2" />
                                Copy Link
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleOpenSendDialog(deck)}>
                                <Send className="w-4 h-4 mr-2" />
                                Send via Email
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem onClick={() => handleDuplicateDeck(deck)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteDeck(deck)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Analytics Preview */}
                    {(deck.status === 'published' || deck.status === 'sent') && (
                      <div className="mt-3 pt-3 border-t border-border flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1" title="Prospect views">
                          <Eye className="w-3 h-3" />
                          {deck.analytics?.prospect_views || 0} views
                        </span>
                        {deck.analytics?.last_prospect_view && (
                          <span className="flex items-center gap-1" title="Last viewed">
                            <Clock className="w-3 h-3" />
                            {new Date(deck.analytics.last_prospect_view).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="mt-4 flex gap-2">
                      <Button 
                        asChild 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                      >
                        <Link to={`/vision-decks/${deck.id}/edit`}>
                          Edit
                        </Link>
                      </Button>
                      {deck.status === 'published' && (
                        <Button 
                          size="sm" 
                          className="flex-1 bg-[#B89B5E] hover:bg-[#A08050] text-white"
                          onClick={() => handleOpenSendDialog(deck)}
                          data-testid={`send-deck-btn-${deck.id}`}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Send
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Send Deck Dialog */}
      <Dialog open={sendDeckDialog} onOpenChange={setSendDeckDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#B89B5E]" />
              Send Vision Deck
            </DialogTitle>
            <DialogDescription>
              Send "{deckToSend?.name}" via email with a direct link to view
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Deck Preview */}
            <div className="p-3 bg-[#F7F5F2] rounded-lg flex items-center gap-3">
              <div className="w-12 h-12 bg-[#B89B5E]/20 rounded-lg flex items-center justify-center">
                <Crown className="w-6 h-6 text-[#B89B5E]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[#1C1C1C]">{deckToSend?.name}</p>
                <p className="text-sm text-[#6B7280]">{deckToSend?.pages?.length || 0} pages</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => deckToSend && handleCopyLink(deckToSend)}
              >
                <Link2 className="w-4 h-4 mr-1" />
                Copy Link
              </Button>
            </div>

            {/* Recipient Email */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">
                Recipient Email *
              </label>
              <Input
                type="email"
                placeholder="client@company.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                data-testid="send-deck-email-input"
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">
                Subject *
              </label>
              <Input
                placeholder="Email subject..."
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                data-testid="send-deck-subject-input"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">
                Message
              </label>
              <Textarea
                placeholder="Add a personal message..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={4}
                className="resize-none"
                data-testid="send-deck-message-input"
              />
              <p className="text-xs text-[#6B7280] mt-1">
                The deck link will be automatically added to your message.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setSendDeckDialog(false);
                setDeckToSend(null);
                setRecipientEmail('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendDeck}
              disabled={sendingDeck || !recipientEmail.trim() || !emailSubject.trim()}
              className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
              data-testid="send-deck-confirm-btn"
            >
              {sendingDeck ? (
                <>Sending...</>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
